export const DIAGRAM_LEVEL_KEYS = [
  "Influences organisationnelles",
  "Facteurs de supervision",
  "Conditions préalables",
  "Actes dangereux",
];

// Assigns a column index (0..4, last = Incident) and a row index to each factor,
// so that factors chained by causal links line up as horizontally as possible.
// The longest / most-validated chain is laid out first so it keeps priority on rows.
// Factors not involved in any link get no row assignment (caller stacks them below).
export function computeCausalLayout(factors, links) {
  const levelIndex = {};
  DIAGRAM_LEVEL_KEYS.forEach((lvl, i) => { levelIndex[lvl] = i; });

  const factorById = {};
  factors.forEach((f) => { factorById[f.ID_FACTEUR] = f; });

  const colOf = (id) => {
    if (id === "INCIDENT") return DIAGRAM_LEVEL_KEYS.length;
    const f = factorById[id];
    return f ? levelIndex[f.NIVEAU] : null;
  };

  const outgoing = {};
  const linkedIds = new Set();
  links.forEach((l) => {
    if (colOf(l.ID_SOURCE) === null || colOf(l.ID_CIBLE) === null) return;
    outgoing[l.ID_SOURCE] = outgoing[l.ID_SOURCE] || [];
    outgoing[l.ID_SOURCE].push({ to: l.ID_CIBLE, validated: l.STATUT_LIEN === "Validé", linkId: l.ID_LIEN });
    linkedIds.add(l.ID_SOURCE);
    linkedIds.add(l.ID_CIBLE);
  });

  const allTargets = new Set(links.map((l) => l.ID_CIBLE));
  const starts = Object.keys(outgoing).filter((id) => !allTargets.has(id));

  const chainMetric = (startId) => {
    let best = { depth: 0, validatedCount: 0, linkIds: [], nodeIds: [startId] };
    const walk = (id, depth, validatedCount, linkIds, nodeIds, visited) => {
      if (depth > best.depth || (depth === best.depth && validatedCount > best.validatedCount)) {
        best = { depth, validatedCount, linkIds, nodeIds };
      }
      (outgoing[id] || []).forEach(({ to, validated, linkId }) => {
        if (visited.has(to)) return;
        walk(to, depth + 1, validatedCount + (validated ? 1 : 0), [...linkIds, linkId], [...nodeIds, to], new Set(visited).add(to));
      });
    };
    walk(startId, 0, 0, [], [startId], new Set([startId]));
    return best;
  };

  starts.sort((a, b) => {
    const ma = chainMetric(a);
    const mb = chainMetric(b);
    if (mb.validatedCount !== ma.validatedCount) return mb.validatedCount - ma.validatedCount;
    if (mb.depth !== ma.depth) return mb.depth - ma.depth;
    return (colOf(a) ?? 99) - (colOf(b) ?? 99);
  });

  const rowByCol = {};
  const rowAssigned = {};
  const takeRow = (col, preferred) => {
    const used = rowByCol[`used_${col}`] || new Set();
    rowByCol[`used_${col}`] = used;
    if (preferred !== undefined && !used.has(preferred)) {
      used.add(preferred);
      return preferred;
    }
    let r = 0;
    while (used.has(r)) r += 1;
    used.add(r);
    return r;
  };

  const visited = new Set();
  const visit = (id) => {
    if (visited.has(id)) return;
    visited.add(id);
    const col = colOf(id);
    if (col === null) return;
    if (!(id in rowAssigned)) rowAssigned[id] = takeRow(col);
    const row = rowAssigned[id];
    (outgoing[id] || []).forEach(({ to: targetId }) => {
      const targetCol = colOf(targetId);
      if (targetCol === null) return;
      if (!(targetId in rowAssigned)) rowAssigned[targetId] = takeRow(targetCol, row);
      visit(targetId);
    });
  };
  starts.forEach(visit);
  linkedIds.forEach((id) => {
    if (!(id in rowAssigned)) {
      const col = colOf(id);
      if (col !== null) rowAssigned[id] = takeRow(col);
    }
  });

  const maxRow = Object.values(rowAssigned).reduce((m, r) => Math.max(m, r), -1);
  const bestChain = starts.length ? chainMetric(starts[0]) : { linkIds: [], nodeIds: [] };
  const mainChainLinkIds = new Set(bestChain.linkIds);
  const mainChainNodeIds = new Set(bestChain.nodeIds);
  return { rowAssigned, linkedIds, colOf, maxRow, mainChainLinkIds, mainChainNodeIds };
}
